import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { useT, LanguageSwitcher } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";

const BG = "https://static.prod-images.emergentagent.com/jobs/a38c17ed-f1e9-46fe-9e7c-fe4731e3e917/images/585c338ce7226b5954ccd5e96883f9bac1c70cbfa6424f609ab458b88f5c70dc.jpeg";

export default function Login() {
  const { login } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const goBack = () => (window.history.state?.idx > 0 ? nav(-1) : nav("/"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="absolute top-4 right-4 z-20"><LanguageSwitcher /></div>
      <div className="hidden md:block relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 scale-105 blur-[1px]" />
        <div className="absolute inset-0 bg-tatami-overlay" />
        <div className="absolute inset-0 bg-kukkiwon-mesh opacity-70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div>
            <button type="button" onClick={goBack} className="flex items-center gap-2 font-heading font-extrabold text-2xl tracking-tight text-foreground hover:opacity-80 transition-opacity" data-testid="login-logo-back">
              <Logo size={30} className="text-brand-yellow" /> KIHAP
            </button>
            <div className="text-muted-foreground text-xs uppercase tracking-[0.25em] mt-1 ml-9">Tournament Software for Taekwondo</div>
          </div>
          <div>
            <h1 className="font-heading text-6xl md:text-7xl font-black uppercase leading-[0.9] tracking-tight">Taekwondo<br />tournaments,<br /><span className="text-brand-yellow">simplified!</span></h1>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <button type="button" onClick={goBack} className="md:hidden flex items-center gap-2 font-heading font-extrabold text-xl mb-8 text-foreground" data-testid="login-logo-back-mobile">
              <Logo size={24} className="text-brand-yellow" /> KIHAP
            </button>
            <h2 className="font-heading text-3xl font-bold uppercase tracking-tight">{t("login.title")}</h2>
            <p className="text-muted-foreground mt-1 text-sm">{t("login.subtitle")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t("login.identifier")}</Label>
            <Input id="email" data-testid="login-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("common.password")}</Label>
            <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
          <Button type="submit" className="w-full h-11 font-semibold" data-testid="login-submit" disabled={loading}>
            {loading ? t("login.accessing") : t("login.enter")}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            {t("login.noAccount")} <Link to="/register" className="text-white underline underline-offset-4">{t("login.register")}</Link>
          </p>
          <p className="text-sm text-muted-foreground text-center">
            <Link to="/forgot-password" className="underline underline-offset-4" data-testid="forgot-link">{t("login.forgot")}</Link>
          </p>
          <p className="text-sm text-muted-foreground text-center pt-6 mt-2 border-t border-border/60">
            {t("login.areYouRing")} <Link to="/ring-login" className="underline underline-offset-4">{t("login.ringLogin")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
